import { Fragment } from "react";

const EMPTY_VALUE_DISPLAY = "—";

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE_DISPLAY;
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}

export type ApprovalRowValue =
  | { kind: "text"; value: unknown }
  | { kind: "link"; href: string; text: string };

const DEFAULT_TITLE = "Action requires your approval";
const DEFAULT_APPROVE_LABEL = "Approve";
const DEFAULT_DENY_LABEL = "Deny";
const SKIP_LABEL = "Skip";

interface ToolApprovalCardProps {
  rows: { label: string; value: ApprovalRowValue }[];
  onApprove?: (
    decision: "approved" | "denied",
    clientArtifacts?: Record<string, string>,
  ) => void;
  onSkip?: () => void;
  isLoading: boolean;
  primaryColor: string;
  error?: string;
  disclaimer?: string;
  isApproveDisabled?: boolean;
  approveLabel?: string;
  denyLabel?: string;
  title?: string;
  onLinkClick?: (url: string, linkText: string) => void;
}

export function ToolApprovalCard({
  rows,
  onApprove,
  onSkip,
  isLoading,
  primaryColor,
  error,
  disclaimer,
  isApproveDisabled,
  approveLabel,
  denyLabel,
  title,
  onLinkClick,
}: ToolApprovalCardProps) {
  const hasError = !!error;
  const isApproveButtonDisabled = isLoading || hasError || !!isApproveDisabled;
  const displayTitle = title ?? DEFAULT_TITLE;

  return (
    <div
      className="rounded-xl border border-[#E0E0E0] bg-white p-4 shadow-sm"
      role="region"
      aria-label={displayTitle}
      data-testid="approval-card"
    >
      <p className="mb-3 text-sm font-semibold text-[#232323]">
        {displayTitle}
      </p>
      {rows.length > 0 && (
        <dl className="mb-4 grid grid-cols-[8rem_1fr]">
          {rows.map(({ label, value }, index) => (
            <Fragment key={`${index}-${label}`}>
              <dt className="border-t border-[#E0E0E0] py-2 text-sm text-[#5A5A5A]">
                {label}
              </dt>
              <dd className="border-t border-[#E0E0E0] py-2 text-sm text-[#232323]">
                {value.kind === "link" ? (
                  <a
                    href={value.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={() => onLinkClick?.(value.href, value.text)}
                  >
                    {value.text}
                    <span className="sr-only"> opens in new tab</span>
                  </a>
                ) : (
                  formatValue(value.value)
                )}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
      {hasError && (
        <p className="mb-3 text-xs text-red-600" data-testid="approval-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        {!hasError && (
          <button
            onClick={() => onApprove?.("approved")}
            disabled={isApproveButtonDisabled}
            style={{ backgroundColor: primaryColor }}
            className="rounded px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Approve action"
          >
            {approveLabel ?? DEFAULT_APPROVE_LABEL}
          </button>
        )}
        <button
          onClick={() => onApprove?.("denied")}
          disabled={isLoading}
          className="rounded border border-[#E0E0E0] bg-white px-4 py-2 text-sm font-medium text-[#232323] transition-colors hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Deny action"
        >
          {denyLabel ?? DEFAULT_DENY_LABEL}
        </button>
        {hasError && onSkip && (
          <button
            onClick={onSkip}
            className="rounded border border-[#E0E0E0] bg-white px-4 py-2 text-sm font-medium text-[#5A5A5A] transition-colors hover:bg-[#F5F5F5]"
            aria-label="Skip approval"
          >
            {SKIP_LABEL}
          </button>
        )}
      </div>
      {disclaimer && (
        <p className="mt-2 text-xs text-[#5A5A5A]">{disclaimer}</p>
      )}
    </div>
  );
}
